import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Avatar,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight, VpnKey } from "@mui/icons-material";
import logo from "../../assets/Arconest_logo.png";

import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { url } from "../../constants/url";
import google from "../../assets/google.png";
import phone from "../../assets/mobile.png";
import { useAuth } from '../../context/authContext';
import { Icon } from '../../icons';

const AuthLanding = () => {
  const navigate = useNavigate();
  const { signInWithGoogle, signInWithTestCredentials, authLoading } = useAuth();
  const [error, setError] = useState(null);

  const [isTestLoginOpen, setIsTestLoginOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testPassword, setTestPassword] = useState('');
  const [testError, setTestError] = useState(null);
  const [testSubmitting, setTestSubmitting] = useState(false);

  const handleTestLoginSubmit = async (e) => {
    e.preventDefault();
    if (!testEmail || !testPassword) {
      setTestError("Please enter both email and password.");
      return;
    }
    try {
      setTestSubmitting(true);
      setTestError(null);
      const result = await signInWithTestCredentials(testEmail, testPassword);
      if (result.success) {
        setIsTestLoginOpen(false);
        navigate('/dashboard');
      }
    } catch (err) {
      setTestError(err.message || "Failed to log in. Please check credentials.");
    } finally {
      setTestSubmitting(false);
    }
  };

  const [clientEngineerStats, setClientEngineerStats] = useState(null);

  useEffect(() => {
    const fetchClientEngineerStats = async () => {
      try {
        const res = await axios.get(`${url}/api/dashboard/client-engineer-stats`);
        setClientEngineerStats(res.data);
      } catch (err) {
        console.error("Failed to fetch client/engineer stats", err);
      }
    };

    fetchClientEngineerStats();
  }, []);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // below 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900px - 1200px

  // Testimonials data for carousel
  const testimonials = [
    {
      id: 1,
      text: "Arconest has revolutionized our project management workflow. The real-time tracking and seamless communication between engineers and clients have significantly improved our delivery timelines.",
      name: "Rajesh Deshmukh",
      role: "Project Director, BuildRight Infra"
    },
    {
      id: 2,
      text: "The field inspection tools provided by this platform are exceptional. They've eliminated manual paperwork and allowed our site engineers to focus on quality and safety.",
      name: "Sandeep Patil",
      role: "Senior Site Engineer"
    },
    {
      id: 3,
      text: "As a developer, transparency is key. Arconest provides a clear window into every phase of our construction projects, giving us the confidence we need to scale.",
      name: "Vikram Malhotra",
      role: "CEO, Urban Living Spaces"
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleNext = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithGoogle();
      
      if (result.success) {
        if (result.exists) {
          navigate('/dashboard');
        } else {
          navigate('/register', { 
            state: { 
              email: result.firebaseUser.email,
              name: result.firebaseUser.displayName,
              isGoogleLogin: true,
              role: result.role || null
            } 
          });
        }
      }
    } catch (error) {
      setError('Failed to sign in with Google. Please try again.');
      console.error('Google login error:', error);
    }
  };

  const handleStartAs = (role) => {
    navigate('/register', { 
      state: { 
        role: role,
        fromLanding: true
      } 
    });
  };

  // Get appropriate padding based on screen size
  const getContainerPadding = () => {
    if (isMobile) return { xs: 2, sm: 3 };
    if (isTablet) return { md: 4, lg: 4 };
    return { lg: 6, xl: 8 };
  };

  // Get header button text based on screen size
  const getButtonText = (fullText) => {
    if (isMobile) {
      return fullText.includes('Engineer') ? 'Engineer' : 'Client';
    }
    return fullText;
  };

  return (
    <Box 
      minHeight="100vh" 
      bgcolor={COLORS.BACKGROUND}
      sx={{
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* ================= TOP HEADER ================= */}
      <Box
        px={getContainerPadding()}
        py={{ xs: 1.5, md: 2 }}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          flexWrap: 'nowrap',
          gap: { xs: 1, sm: 2 }
        }}
      >
        {/* LOGO */}
        <Box 
          display="flex" 
          alignItems="center"
          sx={{
            flexShrink: 1,
            minWidth: { xs: '120px', sm: '140px' }
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Arconest Consultants Logo"
            sx={{
              height: { xs: 28, sm: 32, md: 40, lg: 44 },
              width: "auto",
              objectFit: "contain"
            }}
          />
        </Box>

        {/* Start As - Responsive Container */}
        <Box 
          display="flex" 
          alignItems="center" 
          gap={{ xs: 0.5, sm: 1.5, md: 2 }}
          sx={{
            flexShrink: 0,
            flexWrap: 'nowrap'
          }}
        >
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              fontFamily: FONT_FAMILY,
              color: COLORS.TEXT,
              whiteSpace: 'nowrap'
            }}
          >
            Start As:
          </Typography>

          <Button
            onClick={() => handleStartAs('ENGINEER')}
            variant="contained"
            size="small"
            sx={{
              bgcolor: COLORS.PRIMARY,
              borderRadius: 1,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              ...TYPOGRAPHY.H4,
              px: { xs: 0.75, sm: 1.5, md: 2 },
              py: { xs: 0.25, sm: 0.5, md: 0.75 },
              minWidth: { xs: '70px', sm: '100px', md: '120px' },
              whiteSpace: 'nowrap',
              height: { xs: 28, sm: 32, md: 36 }
            }}
          >
            {getButtonText('An Engineer')}
          </Button>

          <Button
            variant="contained"
            onClick={() => handleStartAs('CLIENT')}
            size="small"
            sx={{
              bgcolor: COLORS.SECONDARY,
              borderRadius: 1,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              ...TYPOGRAPHY.H4,
              px: { xs: 0.75, sm: 1.5, md: 2 },
              py: { xs: 0.25, sm: 0.5, md: 0.75 },
              minWidth: { xs: '60px', sm: '90px', md: '110px' },
              whiteSpace: 'nowrap',
              height: { xs: 28, sm: 32, md: 36 }
            }}
          >
            {getButtonText('A Client')}
          </Button>
        </Box>
      </Box>

      {/* ================= MAIN CONTENT ================= */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          px: getContainerPadding(),
          py: { xs: 3, md: 4 },
          boxSizing: 'border-box',
          alignItems: 'center'
        }}
      >
        {/* ================= LEFT COLUMN ================= */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            display: 'flex',
            flexDirection: 'column',
            pr: { md: 4 },
            mb: { xs: 4, md: 0 }
          }}
        >
          <Typography
            sx={{
              ...TYPOGRAPHY.H2,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
              mb: { xs: 2, md: 3 },
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            #1 The Construction <br /> Consultants!
          </Typography>

          {/* Process Box - This creates empty space on the right */}
          <Box
            sx={{
              height: { 
                xs: '200px', 
                sm: '240px', 
                md: '280px',
                lg: '320px' 
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: { xs: 2, md: 3 },
              borderRadius: { xs: 2, md: 3 },
              width: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                gap: 2,
                justifyContent: 'space-between',
                //alignItems: 'center',
                    alignItems: 'stretch',   // ✅ ADD THIS

                px: { xs: 1, md: 2 }
              }}
            >
              <Box sx={{ flex: 1, bgcolor: COLORS.PRIMARY, borderRadius: 2, p: 2 ,    height: '100%'    // ✅ ADD THIS
}}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Icon name="clients" size={40} color={COLORS.BACKGROUND} />
                  <Box sx={{ textAlign: 'right', flex: 1 }}>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.BACKGROUND }}>
                      {clientEngineerStats?.totalClients ?? 0}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.BACKGROUND }}>
                      Total Clients
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ flex: 1, bgcolor: 'white', borderRadius: 2, p: 2, border: '2px solid ' + COLORS.PRIMARY, boxSizing: 'border-box' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Icon name="engineers" size={40} color={COLORS.PRIMARY} />
                  <Box sx={{ textAlign: 'right', flex: 1 }}>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
                      {clientEngineerStats?.totalEngineers ?? 0}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT }}>
                      Total Engineers
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ flex: 1, bgcolor: 'white', borderRadius: 2, p: 2, border: '2px solid ' + COLORS.PRIMARY, boxSizing: 'border-box' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Icon name="projects" size={40} color={COLORS.PRIMARY} />
                  <Box sx={{ textAlign: 'right', flex: 1 }}>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
                      {clientEngineerStats?.totalRequested ?? 0}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT }}>
                      Requested Projects
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Testimonials Carousel - Aligned with Phone Login */}
          <Box
            sx={{
              width: '100%',
              maxWidth: { md: '90%' }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                minHeight: '140px',
                mb: 1
              }}
            >
              {testimonials.map((testimonial, index) => (
                <Box
                  key={testimonial.id}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: index === currentTestimonial ? 1 : 0,
                    transition: 'opacity 0.5s ease-in-out',
                    pointerEvents: index === currentTestimonial ? 'auto' : 'none'
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      opacity: 0.8,
                      mb: { xs: 1.5, md: 2 },
                      textAlign: { xs: 'center', md: 'left' },
                      lineHeight: 1.6
                    }}
                  >
                    {testimonial.text}
                  </Typography>

                  {/* Founder Section */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: { xs: 'center', md: 'flex-start' }
                    }}
                  >
                    <Avatar sx={{ 
                      width: { xs: 24, md: 30 }, 
                      height: { xs: 24, md: 30 } 
                    }} />
                    <Box>
                      <Typography
                        component="div"
                        sx={{
                          ...TYPOGRAPHY.H4,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.PRIMARY,
                          fontWeight: 600
                        }}
                      >
                        <strong>{testimonial.name}</strong>
                      </Typography>
                      <Typography
                        component="div"
                        sx={{
                          ...TYPOGRAPHY.CAPTION_MICRO,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY
                        }}
                      >
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Carousel Controls */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mt: { xs: -3, md: -5 }
              }}
            >
              <IconButton
                onClick={handlePrev}
                size="small"
                sx={{
                  color: COLORS.PRIMARY,
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                  padding: 0.5
                }}
              >
                <KeyboardArrowLeft fontSize="small" />
              </IconButton>

              {/* Dots Indicator */}
              <Box display="flex" gap={1}>
                {testimonials.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: index === currentTestimonial ? COLORS.PRIMARY : COLORS.LIGHT_SECONDARY,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  />
                ))}
              </Box>

              <IconButton
                onClick={handleNext}
                size="small"
                sx={{
                  color: COLORS.PRIMARY,
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                  padding: 0.5
                }}
              >
                <KeyboardArrowRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

      {/* ================= RIGHT COLUMN ================= */}
<Box
  sx={{
    width: { xs: '100%', md: '50%' },
    display: 'flex',
    flexDirection: 'column',
    pl: { md: 4 },
    justifyContent: 'center',
  }}
>
  {/* TOP PART: Login with Google - Aligned with Left Title */}
  <Box
    sx={{
      width: '100%',
      maxWidth: { xs: '100%', sm: '400px', md: '100%' },
      mx: { xs: 'auto', md: 0 },
      display: 'flex',
      flexDirection: 'column',
      mb: { xs: 4, md: 0 },
      pt: { md: 0 }
    }}
  >
    <Box
      sx={{
        textAlign: "center",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Typography
        sx={{
          ...TYPOGRAPHY.H2,
          fontFamily: FONT_FAMILY,
          color: COLORS.PRIMARY,
          mb: { xs: 3, md: 4 },
          textAlign: 'center',
          width: '100%'
        }}
      >
        Login to Portal
      </Typography>

      <Button
        variant="contained"
        onClick={() => setIsTestLoginOpen(true)}
        disabled={authLoading || testSubmitting}
        startIcon={
          <VpnKey sx={{ color: '#ffffff' }} />
        }
        sx={{
          width: { xs: '100%', sm: '294px', md: '294px' },
          maxWidth: '100%',
          height: { xs: 44, md: 50 },
          borderRadius: "7px",
          textTransform: "none",
          fontFamily: FONT_FAMILY,
          color: '#ffffff',
          backgroundColor: COLORS.SECONDARY,
          ...TYPOGRAPHY.H4,
          mb: 2,
          "&:hover": {
            backgroundColor: COLORS.PRIMARY,
          }
        }}
      >
        Test Login
      </Button>

      <Button
        variant="outlined"
        onClick={handleGoogleLogin}
        disabled={authLoading}
        startIcon={
          <Box 
            component="img" 
            src={google} 
            alt="Google" 
            sx={{ 
              width: { xs: 18, md: 20 }, 
              height: { xs: 18, md: 20 } 
            }}
          />
        }
        sx={{
          width: { xs: '100%', sm: '294px', md: '294px' },
          maxWidth: '100%',
          height: { xs: 44, md: 50 },
          borderRadius: "7px",
          borderWidth: "0.2px",
          textTransform: "none",
          fontFamily: FONT_FAMILY,
          color: COLORS.PRIMARY,
          borderColor: COLORS.LIGHT_SECONDARY,
          ...TYPOGRAPHY.H4,
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
            borderColor: COLORS.SECONDARY
          }
        }}
      >
        {authLoading ? 'Signing in...' : 'Login with Google'}
      </Button>
    </Box>
  </Box>
  
  {/* BOTTOM PART: Login with Phone - Aligned with Testimonial */}
  <Box
    sx={{
      width: '100%',
      maxWidth: { xs: '100%', sm: '400px', md: '100%' },
      mx: { xs: 'auto', md: 0 },
      display: 'flex',
      flexDirection: 'column',
      mt: { xs: 4, md: 6 },
    }}
  >
    <Box
      sx={{
        textAlign: "center",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Divider sx={{ 
        width: { xs: '100%', sm: '294px', md: '294px' },
        maxWidth: '100%',
        mx: "auto", 
        mb: 3,
        borderColor: COLORS.LIGHT_SECONDARY 
      }} />

      <Button
        variant="outlined"
        onClick={() => navigate("/otp-login")}
        startIcon={
          <Box 
            component="img" 
            src={phone} 
            alt="Phone" 
            sx={{ 
              width: { xs: 18, md: 20 }, 
              height: { xs: 18, md: 20 } 
            }}
          />
        }
        sx={{
          width: { xs: '100%', sm: '294px', md: '294px' },
          maxWidth: '100%',
          height: { xs: 44, md: 50 },
          borderRadius: "7px",
          borderWidth: "0.2px",
          textTransform: "none",
          fontFamily: FONT_FAMILY,
          color: COLORS.PRIMARY,
          borderColor: COLORS.LIGHT_SECONDARY,
          ...TYPOGRAPHY.H4,
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
            borderColor: COLORS.SECONDARY
          }
        }}
      >
        Login with Phone No
      </Button>

      <Typography
        sx={{
          ...TYPOGRAPHY.BODY_SMALL,
          fontFamily: FONT_FAMILY,
          color: COLORS.TEXT,
          opacity: 0.7,
          mt: 3,
          textAlign: 'center'
        }}
      >
        Powered by Arconest Consultants
      </Typography>
    </Box>
  </Box>
</Box>

        {/* Test Login Dialog */}
        <Dialog 
          open={isTestLoginOpen} 
          onClose={() => {
            if (!testSubmitting) {
              setIsTestLoginOpen(false);
              setTestError(null);
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: 2,
              width: '100%',
              maxWidth: '400px'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontFamily: FONT_FAMILY, 
            fontWeight: 600, 
            color: COLORS.PRIMARY,
            textAlign: 'center',
            pb: 1
          }}>
            Test Login
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ 
              fontFamily: FONT_FAMILY, 
              fontSize: '14px', 
              color: 'text.secondary', 
              mb: 3, 
              textAlign: 'center' 
            }}>
              Please enter your test credentials to log in.
            </Typography>
            
            {testError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                {testError}
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              disabled={testSubmitting}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: COLORS.LIGHT_SECONDARY,
                  },
                  '&:hover fieldset': {
                    borderColor: COLORS.SECONDARY,
                  },
                },
              }}
            />
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              variant="outlined"
              value={testPassword}
              onChange={(e) => setTestPassword(e.target.value)}
              disabled={testSubmitting}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: COLORS.LIGHT_SECONDARY,
                  },
                  '&:hover fieldset': {
                    borderColor: COLORS.SECONDARY,
                  },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => {
                setIsTestLoginOpen(false);
                setTestError(null);
              }} 
              disabled={testSubmitting}
              sx={{ 
                color: 'text.secondary',
                textTransform: 'none',
                fontFamily: FONT_FAMILY
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleTestLoginSubmit}
              variant="contained"
              disabled={testSubmitting}
              sx={{ 
                backgroundColor: COLORS.PRIMARY,
                textTransform: 'none',
                fontFamily: FONT_FAMILY,
                '&:hover': {
                  backgroundColor: COLORS.SECONDARY
                }
              }}
            >
              {testSubmitting ? 'Logging in...' : 'Login'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AuthLanding;